import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/Button'
import { useModal } from '@/hooks/useModal'
import { useToast } from '@/hooks/useToast'
import {
  createEventPole,
  extractApiErrorMessage,
  updateEventPole,
} from '@/services/events'
import { EventPoleProps, PoleMutationPayloadProps } from '@/types/eventsProps'

interface FormEventPoleProps {
  eventId: string
  pole?: EventPoleProps
  onSaved: () => Promise<void> | void
}

interface PoleFormValues {
  name: string
  description: string
  isMain: boolean
  sortOrder: string
  placeName: string
  addressLine: string
  neighborhood: string
  city: string
  state: string
  latitude: string
  longitude: string
  mapsUrl: string
  startDate: string
  endDate: string
  officialPlaylistId: string
}

function toDateTimeLocal(value?: string | null) {
  if (!value) return ''

  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) {
    return ''
  }

  const timezoneOffset = parsedDate.getTimezoneOffset()
  const localDate = new Date(parsedDate.getTime() - timezoneOffset * 60000)
  return localDate.toISOString().slice(0, 16)
}

function toIsoDateTime(value: string) {
  if (!value) return undefined

  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) {
    return undefined
  }

  return parsedDate.toISOString()
}

function toOptionalNumber(value: string) {
  if (!value.trim()) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

export function FormEventPole({ eventId, pole, onSaved }: FormEventPoleProps) {
  const { closeModal } = useModal()
  const { showToast } = useToast()
  const [isLoading, setIsLoading] = useState(false)

  const { register, handleSubmit, setValue } = useForm<PoleFormValues>({
    defaultValues: {
      name: '',
      description: '',
      isMain: false,
      sortOrder: '',
      placeName: '',
      addressLine: '',
      neighborhood: '',
      city: '',
      state: '',
      latitude: '',
      longitude: '',
      mapsUrl: '',
      startDate: '',
      endDate: '',
      officialPlaylistId: '',
    },
  })

  const onSubmit = async (data: PoleFormValues) => {
    const payload: PoleMutationPayloadProps = {
      name: data.name,
      description: data.description,
      isMain: data.isMain,
      sortOrder: toOptionalNumber(data.sortOrder),
      placeName: data.placeName,
      addressLine: data.addressLine,
      neighborhood: data.neighborhood,
      city: data.city,
      state: data.state,
      latitude: toOptionalNumber(data.latitude),
      longitude: toOptionalNumber(data.longitude),
      mapsUrl: data.mapsUrl,
      startDate: toIsoDateTime(data.startDate),
      endDate: toIsoDateTime(data.endDate),
      officialPlaylistId: data.officialPlaylistId,
    }

    try {
      setIsLoading(true)

      if (pole?.id) {
        await updateEventPole(eventId, pole.id, payload)
        showToast('Polo atualizado com sucesso.', {
          type: 'success',
          theme: 'light',
        })
      } else {
        await createEventPole(eventId, payload)
        showToast('Polo criado com sucesso.', {
          type: 'success',
          theme: 'light',
        })
      }

      await Promise.resolve(onSaved())
      closeModal()
    } catch (error) {
      showToast(
        extractApiErrorMessage(error, 'Nao foi possivel salvar o polo.'),
        {
          type: 'error',
          theme: 'light',
        },
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!pole) return

    setValue('name', pole.name ?? '')
    setValue('description', pole.description ?? '')
    setValue('isMain', Boolean(pole.isMain))
    setValue('sortOrder', pole.sortOrder ? `${pole.sortOrder}` : '')
    setValue('placeName', pole.placeName ?? '')
    setValue('addressLine', pole.addressLine ?? '')
    setValue('neighborhood', pole.neighborhood ?? '')
    setValue('city', pole.city ?? '')
    setValue('state', pole.state ?? '')
    setValue('latitude', pole.latitude !== null ? `${pole.latitude ?? ''}` : '')
    setValue(
      'longitude',
      pole.longitude !== null ? `${pole.longitude ?? ''}` : '',
    )
    setValue('mapsUrl', pole.mapsUrl ?? '')
    setValue('startDate', toDateTimeLocal(pole.startDate))
    setValue('endDate', toDateTimeLocal(pole.endDate))
    setValue('officialPlaylistId', pole.officialPlaylistId ?? '')
  }, [pole, setValue])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3">
        <div>
          <h4 className="mb-2 text-sm font-bold">Nome</h4>
          <input
            {...register('name', { required: true })}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
            placeholder="Patio de Eventos Luiz Gonzaga"
          />
        </div>
      </div>

      <div>
        <h4 className="mb-2 text-sm font-bold">Descricao</h4>
        <textarea
          {...register('description')}
          rows={3}
          className="w-full resize-none rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
        />
      </div>

      <div className="grid grid-cols-3 gap-3 md:grid-cols-1">
        <div>
          <h4 className="mb-2 text-sm font-bold">Cidade</h4>
          <input
            {...register('city', { required: true })}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
            placeholder="Caruaru"
          />
        </div>
        <div>
          <h4 className="mb-2 text-sm font-bold">Estado (UF)</h4>
          <input
            {...register('state', { required: true, maxLength: 2 })}
            maxLength={2}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm uppercase focus:border-gray-500 focus:outline-none"
            placeholder="PE"
          />
        </div>
        <div>
          <h4 className="mb-2 text-sm font-bold">Ordem</h4>
          <input
            {...register('sortOrder')}
            type="number"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
        <div>
          <h4 className="mb-2 text-sm font-bold">Nome do local</h4>
          <input
            {...register('placeName')}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
            placeholder="Patio de Eventos"
          />
        </div>
        <div>
          <h4 className="mb-2 text-sm font-bold">Endereco</h4>
          <input
            {...register('addressLine')}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
            placeholder="Av. Getulio Vargas"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 md:grid-cols-1">
        <div>
          <h4 className="mb-2 text-sm font-bold">Bairro</h4>
          <input
            {...register('neighborhood')}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
        <div>
          <h4 className="mb-2 text-sm font-bold">Latitude</h4>
          <input
            {...register('latitude')}
            type="number"
            step="any"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
        <div>
          <h4 className="mb-2 text-sm font-bold">Longitude</h4>
          <input
            {...register('longitude')}
            type="number"
            step="any"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
        <div>
          <h4 className="mb-2 text-sm font-bold">Maps URL</h4>
          <input
            {...register('mapsUrl')}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
            placeholder="https://maps.google.com/..."
          />
        </div>
        <div>
          <h4 className="mb-2 text-sm font-bold">Playlist oficial (ID)</h4>
          <input
            {...register('officialPlaylistId')}
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
        <div>
          <h4 className="mb-2 text-sm font-bold">Inicio</h4>
          <input
            {...register('startDate')}
            type="datetime-local"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
        <div>
          <h4 className="mb-2 text-sm font-bold">Fim</h4>
          <input
            {...register('endDate')}
            type="datetime-local"
            className="w-full rounded border border-gray-300 px-4 py-2 text-sm focus:border-gray-500 focus:outline-none"
          />
        </div>
      </div>

      <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-gray-700">
        <input {...register('isMain')} type="checkbox" />
        Polo principal
      </label>

      <Button
        title={pole ? 'Atualizar polo' : 'Criar polo'}
        variant="purple"
        isLoading={isLoading}
      />
    </form>
  )
}
